<template>
  <div class="ca">

    <div class="ca-grid">

      <!-- ════ PANEL 1 — VENTAS POR PRODUCTO ════ -->
      <section class="ca-panel">
        <header class="ca-panel-head">
          <div class="ca-panel-title">
            <span class="ca-chevron">›</span>
            <span>Ventas por producto</span>
          </div>
          <div class="ca-panel-actions">
            <div class="ca-seg">
              <button
                type="button"
                class="ca-seg-btn"
                :class="{ 'is-active': !imagenesEnTabla }"
                @click="imagenesEnTabla = false"
              >
                <i class="fa-regular fa-square"></i>
                <span>Sin IMG</span>
              </button>
              <button
                type="button"
                class="ca-seg-btn"
                :class="{ 'is-active': imagenesEnTabla }"
                @click="imagenesEnTabla = true"
              >
                <i class="fa-regular fa-image"></i>
                <span>Con IMG</span>
              </button>
            </div>
            <button class="ca-icon-btn" @click="descargarCSVProductos" title="Descargar CSV">
              <i class="fa-solid fa-download"></i>
            </button>
          </div>
        </header>

        <div class="ca-table-wrap">
          <table class="ca-table">
            <thead>
              <tr>
                <th class="th-prod">Producto</th>
                <th v-if="imagenesEnTabla" class="th-img"></th>
                <th
                  v-for="(año, aIdx) in años"
                  :key="'p-h-'+año"
                  class="th-num"
                  :class="{ 'is-current': aIdx === 0 }"
                >{{ año }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in tablaVentasPorProducto" :key="'p-'+idx" :class="tendencia(item)">
                <td class="td-prod">
                  <span class="ca-prod-name">{{ item.producto }}</span>
                </td>
                <td v-if="imagenesEnTabla" class="td-img">
                  <img v-if="item.imagen" :src="item.imagen" class="ca-img" />
                </td>
                <td
                  v-for="(año, aIdx) in años"
                  :key="'p-'+idx+'-'+año"
                  class="td-num"
                  :class="{ 'is-current': aIdx === 0 }"
                >
                  <span v-if="aIdx === 0 && item['año_'+año] > 0" class="ca-val">
                    <i class="ca-val__arrow"></i>{{ item['año_'+año] }}
                  </span>
                  <span v-else>{{ item['año_'+año] || 0 }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ════ PANEL 2 — VENTAS POR LIBRAS ════ -->
      <section class="ca-panel">
        <header class="ca-panel-head">
          <div class="ca-panel-title">
            <span class="ca-chevron">›</span>
            <span>Ventas por libras</span>
          </div>
          <div class="ca-panel-actions">
            <button class="ca-icon-btn" @click="descargarCSVLibras" title="Descargar CSV">
              <i class="fa-solid fa-download"></i>
            </button>
          </div>
        </header>

        <div class="ca-table-wrap">
          <table class="ca-table">
            <thead>
              <tr>
                <th class="th-prod">Producto</th>
                <th v-if="imagenesEnTabla" class="th-img"></th>
                <th
                  v-for="(año, aIdx) in años"
                  :key="'l-h-'+año"
                  class="th-num"
                  :class="{ 'is-current': aIdx === 0 }"
                >{{ año }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in tablaVentasPorLibras" :key="'l-'+idx" :class="tendencia(item)">
                <td class="td-prod">
                  <span class="ca-prod-name">{{ item.producto }}</span>
                </td>
                <td v-if="imagenesEnTabla" class="td-img">
                  <img v-if="item.imagen" :src="item.imagen" class="ca-img" />
                </td>
                <td
                  v-for="(año, aIdx) in años"
                  :key="'l-'+idx+'-'+año"
                  class="td-num"
                  :class="{ 'is-current': aIdx === 0 }"
                >
                  <span v-if="aIdx === 0 && parseFloat(item['año_'+año]) > 0" class="ca-val ca-val--money">
                    <i class="ca-val__arrow"></i>£{{ item['año_'+año] }}
                  </span>
                  <span v-else>£{{ item['año_'+año] || '0.00' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import { BTable } from "bootstrap-vue";
import BCardCode from "@core/components/b-card-code";

export default {
  components: {
    BTable,
    BCardCode,
  },
  props: {
    items: Array, // Pedidos actuales
    ordersOlds: Array, // Pedidos antiguos
    inicio: String, // Fecha de inicio del rango
    fin: String, // Fecha de fin del rango
  },
  data() {
    return {
      tablaVentasPorProducto: [],
      camposTabla: [],
      años: [], // Guardamos los años ordenados
      tablaVentasPorLibras: [],
      camposTablaImportes: [],
      imagenesEnTabla: false,
    };
  },
  watch: {
    items: {
      handler() {
        this.generarTablaVentas();
        this.generarTablaImporteVentas();
        //console.log(this.items);
      },
      immediate: true,
    },
    ordersOlds: {
      handler() {
        this.generarTablaVentas();
        this.generarTablaImporteVentas();
      },
      immediate: true,
    },
    imagenesEnTabla() {
      this.generarTablaVentas();
      this.generarTablaImporteVentas();
    },
  },
  methods: {
    generarTablaVentas() {
      const productosPorAño = {};
      const totalesPorAño = {};
      const añosSet = new Set();

      const todosLosPedidos = [...this.items, ...this.ordersOlds];
      const pedidosProcesados = new Set(); // Evita duplicados

      todosLosPedidos.forEach((order) => {
        if (pedidosProcesados.has(order.name)) return;
        pedidosProcesados.add(order.name);

        const year = new Date(order.created_at).getFullYear();
        añosSet.add(year);

        order.line_items.forEach((item) => {
          const nombre = item.name || "Producto desconocido";
          const cantidad = Number(item.quantity) || 0;
          const image = item.image || "";

          if (!productosPorAño[nombre]) productosPorAño[nombre] = {};
          if (!productosPorAño[nombre][year]) productosPorAño[nombre][year] = 0;
          if (!productosPorAño[nombre]["imagen"])
            productosPorAño[nombre]["imagen"] = image;
          productosPorAño[nombre][year] += cantidad;

          if (!totalesPorAño[year]) totalesPorAño[year] = 0;
          totalesPorAño[year] += cantidad;
        });
      });

      this.años = Array.from(añosSet).sort((a, b) => b - a);
      const añoActual = this.años[0];

      this.camposTabla = [];

      if (this.imagenesEnTabla) {
        this.camposTabla.push(
          { key: "producto", label: "Producto" },
          { key: "imagen", label: "IMG", class: "text-center" },
          ...this.años.map((año) => ({
            key: `año_${año}`,
            label: año.toString(),
          }))
        );
      } else {
        this.camposTabla.push(
          { key: "producto", label: "Producto" },
          ...this.años.map((año) => ({
            key: `año_${año}`,
            label: año.toString(),
          }))
        );
      }

      const filas = Object.entries(productosPorAño).map(
        ([nombre, datosPorAño]) => {
          const fila = {
            producto: nombre,
            imagen: datosPorAño["imagen"] || "",
          };
          this.años.forEach((año) => {
            fila[`año_${año}`] = datosPorAño[año] || 0;
          });
          return fila;
        }
      );

      // Crear fila de totales
      const filaTotal = { producto: "TOTAL" };
      this.años.forEach((año) => {
        filaTotal[`año_${año}`] = totalesPorAño[año] || 0;
      });

      // Insertar al inicio
      this.tablaVentasPorProducto = [
        ...filas.sort((a, b) => {
          return (b[`año_${añoActual}`] || 0) - (a[`año_${añoActual}`] || 0);
        }),
      ];

      //console.log(this.tablaVentasPorProducto);
    },

    generarTablaImporteVentas() {
      const importePorProductoYAño = {};
      const totalesPorAño = {};
      const añosSet = new Set();
      const pedidosProcesados = new Set();

      const todosLosPedidos = [...this.items, ...this.ordersOlds];

      todosLosPedidos.forEach((order) => {
        if (pedidosProcesados.has(order.name)) return;
        pedidosProcesados.add(order.name);

        const year = new Date(order.created_at).getFullYear();
        añosSet.add(year);

        order.line_items.forEach((item) => {
          const nombre = item.name || "Producto desconocido";
          const precio = parseFloat(item.price) || 0;
          const cantidad = Number(item.quantity) || 0;
          const total = precio * cantidad;
          const image = item.image || "";

          if (!importePorProductoYAño[nombre])
            importePorProductoYAño[nombre] = {};
          if (!importePorProductoYAño[nombre][year])
            importePorProductoYAño[nombre][year] = 0;
          importePorProductoYAño[nombre][year] += total;

          if (!importePorProductoYAño[nombre]["imagen"])
            importePorProductoYAño[nombre]["imagen"] = image;

          if (!totalesPorAño[year]) totalesPorAño[year] = 0;
          totalesPorAño[year] += total;
        });
      });

      this.años = Array.from(añosSet).sort((a, b) => b - a);
      const añoActual = this.años[0];

      this.camposTablaImportes = [];

      if (this.imagenesEnTabla) {
        this.camposTablaImportes.push(
          { key: "producto", label: "Producto" },
          { key: "imagen", label: "IMG", class: "text-center" },
          ...this.años.map((año) => ({
            key: `año_${año}`,
            label: año.toString(),
          }))
        );
      } else {
        this.camposTablaImportes.push(
          { key: "producto", label: "Producto" },
          ...this.años.map((año) => ({
            key: `año_${año}`,
            label: año.toString(),
          }))
        );
      }

      const filas = Object.entries(importePorProductoYAño).map(
        ([nombre, datosPorAño]) => {
          const fila = {
            producto: nombre,
            imagen: datosPorAño["imagen"] || "",
          };
          this.años.forEach((año) => {
            fila[`año_${año}`] = (datosPorAño[año] || 0).toFixed(2);
          });
          return fila;
        }
      );

      // Crear fila de totales
      const filaTotal = { producto: "TOTAL" };
      this.años.forEach((año) => {
        filaTotal[`año_${año}`] = (totalesPorAño[año] || 0).toFixed(2);
      });

      // Insertar al principio
      this.tablaVentasPorLibras = [
        ...filas.sort((a, b) => {
          return (
            parseFloat(b[`año_${añoActual}`]) -
            parseFloat(a[`año_${añoActual}`])
          );
        }),
      ];
    },

    // 'is-up' / 'is-down' / '' comparando el año actual con el anterior (sirve para unidades y libras)
    tendencia(producto) {
      if (this.años.length < 2) return "";
      const actual = parseFloat(producto[`año_${this.años[0]}`]) || 0;
      const anterior = parseFloat(producto[`año_${this.años[1]}`]) || 0;
      if (actual > anterior) return "is-up";
      if (actual < anterior) return "is-down";
      return "";
    },
    descargarCSVProductos() {
      const lineaFecha = `Datos desde: ${this.inicio} hasta ${this.fin}`;

      // Filtrar campos que no sean "imagen"
      const camposSinImagen = this.camposTabla.filter(
        (field) => field.key !== "imagen"
      );

      // Construir encabezado
      const encabezado = camposSinImagen.map((field) => field.label).join(",");

      // Construir filas de datos
      const filas = this.tablaVentasPorProducto
        .map((row) =>
          camposSinImagen.map((field) => row[field.key] || "0").join(",")
        )
        .join("\n");

      // Concatenar fecha, encabezado y datos
      const csvContent =
        "\uFEFF" + lineaFecha + "\n" + encabezado + "\n" + filas;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "ventas_por_producto.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    descargarCSVLibras() {
      const lineaFecha = `Datos desde: ${this.inicio} hasta ${this.fin}`;

      // Excluir campo "imagen"
      const camposSinImagen = this.camposTablaImportes.filter(
        (field) => field.key !== "imagen"
      );

      // Construir encabezado
      const encabezado = camposSinImagen.map((field) => field.label).join(",");

      // Construir filas de datos
      const filas = this.tablaVentasPorLibras
        .map((row) =>
          camposSinImagen.map((field) => row[field.key] || "0").join(",")
        )
        .join("\n");

      // Concatenar fecha, encabezado y datos. Agregar BOM
      const csvContent =
        "\uFEFF" + lineaFecha + "\n" + encabezado + "\n" + filas;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "ventas_por_libras.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ══════════════════════════════════════════════════════════════
// COMPARACIÓN ANUAL — same tokens as /pedido-taller-vero/fabricando
// ══════════════════════════════════════════════════════════════

$ca-bg:        #F8FAFC;
$ca-surface:   #FFFFFF;
$ca-surface-2: #F1F5F9;
$ca-border:    #E2E8F0;
$ca-divider:   #EEF2F7;
$ca-ink:       #1E293B;
$ca-text:      #1E293B;
$ca-mute:      #64748B;
$ca-soft:      #94A3B8;
$ca-primary:   #3B82F6;
$ca-cta:       #F97316;
$ca-up:        #059669;
$ca-down:      #DC2626;

.ca {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: $ca-text;
  font-feature-settings: "tnum", "zero";

  *, *::before, *::after { box-sizing: border-box; }
}

// ──────────── GRID — two side-by-side panels ────────────
.ca-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;

    .ca-panel:first-child { border-right: 0; border-bottom: 1px solid $ca-border; }
  }
}

.ca-panel {
  background: $ca-surface;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &:first-child { border-right: 1px solid $ca-border; }
}

// ──────────── PANEL HEAD ────────────
.ca-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px 12px;
  border-bottom: 1px solid $ca-divider;
  background: $ca-surface;
  flex-wrap: wrap;
}

.ca-panel-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: $ca-ink;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ca-chevron {
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  font-weight: 800;
  color: $ca-primary;
  line-height: 1;
}

.ca-panel-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

// Segmented toggle (Sin IMG / Con IMG)
.ca-seg {
  display: inline-flex;
  background: $ca-bg;
  border: 1px solid $ca-border;
  border-radius: 8px;
  padding: 3px;
}

.ca-seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: $ca-mute;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 200ms ease;

  i { font-size: 10px; opacity: 0.7; }

  &:hover { color: $ca-ink; }

  &.is-active {
    background: $ca-surface;
    color: $ca-primary;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);

    i { opacity: 1; }
  }
}

// Icon button (download)
.ca-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: $ca-surface;
  border: 1px solid $ca-border;
  border-radius: 8px;
  color: $ca-mute;
  cursor: pointer;
  transition: all 150ms ease;
  font-size: 11px;

  &:hover {
    background: $ca-primary;
    border-color: $ca-primary;
    color: #fff;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  }
}

// ──────────── TABLE ────────────
.ca-table-wrap {
  overflow-x: auto;
  max-height: 520px;
  overflow-y: auto;
}

.ca-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: $ca-surface;
    padding: 11px 14px;
    text-align: left;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $ca-soft;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid $ca-border;
    white-space: nowrap;

    &.th-num {
      text-align: right;
      min-width: 70px;
      font-feature-settings: "tnum";
    }

    &.th-num.is-current { color: $ca-primary; }

    &.th-img { width: 44px; text-align: center; }
    &.th-prod { min-width: 180px; }
  }

  tbody tr {
    transition: background 150ms ease;
    border-bottom: 1px solid $ca-divider;

    &:last-child { border-bottom: 0; }
    &:hover { background: rgba(59, 130, 246, 0.04); }

    td {
      padding: 9px 14px;
      vertical-align: middle;

      &.td-num {
        text-align: right;
        font-family: 'JetBrains Mono', monospace;
        font-size: 12px;
        color: $ca-mute;
        font-feature-settings: "tnum", "zero";
      }
      &.td-num.is-current { color: $ca-text; }

      &.td-img { text-align: center; padding: 4px 8px; }
      &.td-prod { max-width: 220px; }
    }
  }
}

.ca-prod-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 500;
  color: $ca-ink;
}

.ca-img {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid $ca-border;
  background: $ca-surface-2;
}

// ──────────── HIGHLIGHT (current year value) ────────────
.ca-val {
  display: inline-block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: $ca-primary;
  background: rgba(59, 130, 246, 0.10);
  padding: 3px 9px;
  border-radius: 6px;
  min-width: 40px;
  text-align: right;
  font-feature-settings: "tnum";

  &--money { letter-spacing: -0.01em; }

  &__arrow {
    display: none;
    font-style: normal;
    font-size: 9px;
    margin-right: 4px;
    vertical-align: 1px;
  }
}

// Tendencia vs. año anterior: fondo muy suave en toda la fila + chip del año actual
tr.is-up { background: rgba($ca-up, 0.05); &:hover { background: rgba($ca-up, 0.09); } }
tr.is-down { background: rgba($ca-down, 0.04); &:hover { background: rgba($ca-down, 0.08); } }

tr.is-up .ca-val {
  color: $ca-up;
  background: rgba($ca-up, 0.10);
  .ca-val__arrow { display: inline; &::before { content: '▲'; } }
}
tr.is-down .ca-val {
  color: $ca-down;
  background: rgba($ca-down, 0.08);
  .ca-val__arrow { display: inline; &::before { content: '▼'; } }
}

// ──────────── SCROLLBAR ────────────
.ca-table-wrap::-webkit-scrollbar { width: 6px; height: 6px; }
.ca-table-wrap::-webkit-scrollbar-track { background: transparent; }
.ca-table-wrap::-webkit-scrollbar-thumb {
  background: $ca-border;
  border-radius: 10px;
  &:hover { background: $ca-soft; }
}

</style>

<style lang="scss">
.dark-layout .ca {
  .ca-panel { background: #1f1f1f; &:first-child { border-right-color: #2c2c2c; } }
  .ca-panel-head { background: #1f1f1f; border-bottom-color: #2c2c2c; }
  .ca-panel-title { color: #e4e6eb; }
  .ca-seg { background: #161616; border-color: #2c2c2c; }
  .ca-seg-btn.is-active { background: #2c2c2c; color: #fff; }
  .ca-icon-btn { background: #1f1f1f; border-color: #2c2c2c; color: #8e9099; }
  .ca-table thead th { background: #1f1f1f; color: #6e7079; border-bottom-color: #2c2c2c; }
  .ca-table tbody tr { border-bottom-color: #2c2c2c; }
  .ca-table tbody tr:hover { background: rgba(59, 130, 246, 0.08); }
  .ca-table tbody tr td { color: #d0d2d6; }
  .ca-prod-name { color: #e4e6eb; }
  tr.is-up { background: rgba(52, 211, 153, 0.06); }
  tr.is-down { background: rgba(248, 113, 113, 0.06); }
  tr.is-up .ca-val { color: #34D399; background: rgba(52, 211, 153, 0.14); }
  tr.is-down .ca-val { color: #F87171; background: rgba(248, 113, 113, 0.14); }
  .ca-grid { @media (max-width: 1100px) { .ca-panel:first-child { border-bottom-color: #2c2c2c; } } }
}
</style>
